import * as Sentry from '@sentry/react'
import { useCallback, useMemo } from 'react'
import { selectAccountId, selectAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { TrackingEvent, useTrackEventMutation } from '../../../../../store/metrics/metrics'

const TRACKING_ENABLED = import.meta.env.REACT_APP_TRACKING_ENABLED === 'true'

export const useTracking = () => {
  const accountId = useAppSelector(selectAccountId)
  const user = useAppSelector(selectAuthenticatedUser)

  const [mutation] = useTrackEventMutation()

  const trackingClient = useMemo(
    () => ({
      track: (event: TrackingEvent) => {
        if (!TRACKING_ENABLED || !accountId || user?.isGhosted) {
          return
        }
        mutation({ accountId, event }).catch((error) => {
          Sentry.captureException(error)
        })
      },
    }),
    [accountId, mutation, user?.isGhosted],
  )

  const tracked = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Fn extends (...arguments_: any[]) => any>(
      fn: Fn,
      event: Partial<TrackingEvent>,
      options: {
        condition?: boolean
        eventNameExtractor?: (...args: Parameters<Fn>) => string
        eventDetailsExtractor?: (...args: Parameters<Fn>) => Record<string, unknown>
      } = {},
    ) => {
      if (!TRACKING_ENABLED || user?.isGhosted) {
        return (...args: Parameters<Fn>) => fn(...args)
      }
      return (...args: Parameters<Fn>) => {
        const { condition = true, eventNameExtractor, eventDetailsExtractor } = options
        if (condition) {
          const { eventName: originalEventName, eventDetails: originalDetails } = event
          const eventName = eventNameExtractor ? eventNameExtractor(...args) : originalEventName
          const additionalDetails = eventDetailsExtractor ? eventDetailsExtractor(...args) : {}
          if (eventName) {
            trackingClient.track({ eventName, eventDetails: { ...originalDetails, ...additionalDetails } })
          }
        }
        return fn(...args)
      }
    },
    [trackingClient, user],
  )

  return { trackingClient, tracked }
}
